// Funções de normalização para nomes, emails e telefones

/**
 * Valida se o nome contém apenas letras e espaços (sem números) - para pessoas físicas
 */
export const validarNome = (nome: string | null | undefined): boolean => {
  if (!nome) return false;
  
  // Regex que permite apenas letras (incluindo acentos), espaços e alguns caracteres especiais de nomes
  const regexNome = /^[a-zA-ZÀ-ÿ\u00C0-\u017F\s'.-]+$/;
  return regexNome.test(nome.trim());
};

/**
 * Valida nome de empresa - permite números (MEI e outras empresas podem ter números)
 * Para MEI: aceita formato XX.XXX.XXX + "nome do(a) empreendedor(a)"
 */
export const validarNomeEmpresa = (nome: string | null | undefined): boolean => {
  if (!nome) return false;
  
  // Verificar se é formato MEI: XX.XXX.XXX + nome
  const regexMEI = /^(\d{2}\.\d{3}\.\d{3})\s+([a-zA-ZÀ-ÿ\u00C0-\u017F\s'.-]+)$/;
  if (regexMEI.test(nome.trim())) {
    return true; // É MEI válido
  }
  
  // Validação padrão para empresas (sem formato MEI)
  const regexNomeEmpresa = /^[a-zA-ZÀ-ÿ\u00C0-\u017F0-9\s'.,&()-]+$/;
  return regexNomeEmpresa.test(nome.trim());
};

/**
 * Normaliza nomes - primeira letra de cada palavra em maiúscula, remove números se houver
 */
export const normalizarNome = (nome: string | null | undefined): string => {
  if (!nome) return '';
  
  const nomeLimpo = nome.replace(/[^a-zA-ZÀ-ÿ\u00C0-\u017F\s'.-]/g, '');
  
  return nomeLimpo
    .toLowerCase()
    .split(' ')
    .map(palavra => {
      if (!palavra.trim()) return '';
      
      // Ignora preposições e artigos
      if (['de', 'da', 'do', 'dos', 'das', 'e', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem'].includes(palavra.trim())) {
        return palavra.trim();
      }
      return palavra.charAt(0).toUpperCase() + palavra.slice(1);
    })
    .filter(palavra => palavra)
    .join(' ')
    .trim();
};

/**
 * Normaliza nomes de empresas - permite números e caracteres especiais comuns
 * Para MEI: formato XX.XXX.XXX + "nome do(a) empreendedor(a)"
 */
export const normalizarNomeEmpresa = (nome: string | null | undefined): string => {
  if (!nome) return '';
  
  // Detectar se é MEI pelo formato XX.XXX.XXX no início
  const regexMEI = /^(\d{2}\.\d{3}\.\d{3})\s+(.+)$/;
  const matchMEI = nome.match(regexMEI);
  
  if (matchMEI) {
    // É MEI: manter formato XX.XXX.XXX + normalizar nome do empreendedor
    const cpfFormatado = matchMEI[1];
    const nomeEmpreendedor = matchMEI[2];
    
    // Normalizar nome do empreendedor
    const nomeNormalizado = nomeEmpreendedor
      .toLowerCase()
      .split(' ')
      .map(palavra => {
        if (!palavra.trim()) return '';
        // Ignora preposições e artigos (mantém em minúscula)
        if (['de', 'da', 'do', 'dos', 'das', 'e', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem'].includes(palavra.trim())) {
          return palavra.trim();
        }
        return palavra.charAt(0).toUpperCase() + palavra.slice(1);
      })
      .filter(palavra => palavra)
      .join(' ')
      .trim();
    
    return `${cpfFormatado} ${nomeNormalizado}`;
  }
  
  // Não é MEI: normalização padrão de empresa
  const nomeLimpo = nome.replace(/[^a-zA-ZÀ-ÿ\u00C0-\u017F0-9\s'.,&()-]/g, '');
  
  return nomeLimpo
    .toLowerCase()
    .split(' ')
    .map(palavra => {
      if (!palavra.trim()) return '';
      
      // Ignora preposições, artigos e palavras especiais (mantém em minúscula)
      if (['de', 'da', 'do', 'dos', 'das', 'e', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem', 'ltda', 'me', 'mei', 'eireli'].includes(palavra.trim())) {
        return palavra.trim();
      }
      
      // Capitaliza primeira letra, mantendo números intactos
      return palavra.charAt(0).toUpperCase() + palavra.slice(1);
    })
    .filter(palavra => palavra)
    .join(' ')
    .trim();
};

/**
 * Normaliza emails - sempre em minúsculas
 */
export const normalizarEmail = (email: string | null | undefined): string => {
  if (!email) return '';
  return email.toLowerCase().trim();
};

/**
 * Normaliza telefones - formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 */
export const normalizarTelefone = (telefone: string | null | undefined): string => {
  if (!telefone) return '';
  
  const numeros = telefone.replace(/\D/g, '');
  
  // Verifica se tem o tamanho correto
  if (numeros.length === 11) {
    // Celular: (XX) XXXXX-XXXX
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  } else if (numeros.length === 10) {
    // Fixo: (XX) XXXX-XXXX
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  }
  
  // Retorna o telefone original se não for possível formatar
  return telefone;
};

/**
 * Normaliza CPF - remove caracteres não-numéricos e formata XXX.XXX.XXX-XX
 */
export const normalizarCPF = (cpf: string | null | undefined): string => {
  if (!cpf) return '';
  
  const numeros = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (numeros.length === 11) {
    return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-${numeros.slice(9)}`;
  }
  
  // Retorna o CPF original se não for possível formatar
  return cpf;
};

/**
 * Normaliza CNPJ - remove caracteres não-numéricos e formata XX.XXX.XXX/XXXX-XX
 */
export const normalizarCNPJ = (cnpj: string | null | undefined): string => {
  if (!cnpj) return '';
  
  const numeros = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (numeros.length === 14) {
    return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-${numeros.slice(12)}`;
  }
  
  // Retorna o CNPJ original se não for possível formatar
  return cnpj;
};

/**
 * Normaliza CEP - remove caracteres não-numéricos e formata XXXXX-XXX
 */
export const normalizarCEP = (cep: string | null | undefined): string => {
  if (!cep) return '';
  
  const numeros = cep.replace(/\D/g, '');
  
  // Verifica se tem 8 dígitos
  if (numeros.length === 8) {
    return `${numeros.slice(0, 5)}-${numeros.slice(5)}`;
  }
  
  // Retorna o CEP original se não for possível formatar
  return cep;
};

/**
 * Normaliza descrições - remove espaços extras, mantém formatação
 */
export const normalizarDescricao = (descricao: string | null | undefined): string => {
  if (!descricao) return '';
  
  return descricao
    .replace(/\s+/g, ' ') // Substitui múltiplos espaços por um único espaço
    .trim();
};

/**
 * Normaliza texto geral - remove espaços extras e capitaliza primeira letra
 */
export const normalizarTexto = (texto: string | null | undefined): string => {
  if (!texto) return '';
  
  return texto
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .map((palavra: string) => {
      if (['de', 'da', 'do', 'dos', 'das', 'e', 'em', 'na', 'no', 'nas', 'nos', 'para', 'por', 'com', 'sem'].includes(palavra)) {
        return palavra;
      }
      return palavra.charAt(0).toUpperCase() + palavra.slice(1);
    })
    .join(' ');
};

/**
 * Remove apenas caracteres não-numéricos (para armazenar no banco)
 */
export const removerMascaraNumerica = (valor: string | null | undefined): string => {
  if (!valor) return '';
  return valor.replace(/\D/g, '');
};

/**
 * Remove máscara de CPF/CNPJ - alias para removerMascaraNumerica
 */
export const removerMascaraCpfCnpj = (valor: string | null | undefined): string => {
  return removerMascaraNumerica(valor);
};

/**
 * Aplica máscara dinamicamente - CPF (11 dígitos) ou CNPJ (14 dígitos)
 * Detecta automaticamente o tipo baseado no comprimento
 */
export const aplicarMascaraCpfCnpj = (valor: string | null | undefined): string => {
  if (!valor) return '';

  const numeros = valor.replace(/\D/g, '');

  if (numeros.length <= 11) {
    // Aplica máscara de CPF progressivamente
    if (numeros.length <= 3) return numeros;
    if (numeros.length <= 6) return numeros.replace(/^(\d{3})(\d)/, '$1.$2');
    if (numeros.length <= 9) return numeros.replace(/^(\d{3})(\d{3})(\d)/, '$1.$2.$3');
    return numeros.replace(/^(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  } else {
    // Aplica máscara de CNPJ progressivamente
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 5) return numeros.replace(/^(\d{2})(\d)/, '$1.$2');
    if (numeros.length <= 8) return numeros.replace(/^(\d{2})(\d{3})(\d)/, '$1.$2.$3');
    if (numeros.length <= 12) return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d)/, '$1.$2.$3/$4');
    // Limita a 14 dígitos e aplica máscara completa
    const numerosLimitados = numeros.slice(0, 14);
    return numerosLimitados.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
  }
};

/**
 * Valida se é um CPF ou CNPJ válido (apenas formato, não dígito verificador)
 */
export const validarFormatoCpfCnpj = (valor: string | null | undefined): boolean => {
  if (!valor) return false;

  const numeros = valor.replace(/\D/g, '');
  return numeros.length === 11 || numeros.length === 14;
};

/**
 * Detecta se o valor é CPF (11 dígitos) ou CNPJ (14 dígitos)
 */
export const detectarTipoCpfCnpj = (valor: string | null | undefined): 'CPF' | 'CNPJ' | 'INVALIDO' => {
  if (!valor) return 'INVALIDO';

  const numeros = valor.replace(/\D/g, '');

  if (numeros.length === 11) return 'CPF';
  if (numeros.length === 14) return 'CNPJ';
  return 'INVALIDO';
};

/**
 * Formata CPF para exibição (aceita entrada com ou sem máscara)
 * Retorna formato XXX.XXX.XXX-XX
 */
export const formatarCpf = (value: string): string => {
  const numbers = value.replace(/\D/g, '');

  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return numbers.replace(/^(\d{3})(\d{0,3})$/, '$1.$2');
  } else if (numbers.length <= 9) {
    return numbers.replace(/^(\d{3})(\d{3})(\d{0,3})$/, '$1.$2.$3');
  } else if (numbers.length <= 11) {
    return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2})$/, '$1.$2.$3-$4');
  }

  return numbers.slice(0, 11).replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

/**
 * Valida CPF com dígito verificador
 */
export const validarCpf = (cpf: string): boolean => {
  const numeros = cpf.replace(/\D/g, '');

  if (numeros.length !== 11) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(numeros)) return false;

  // Valida primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(numeros.charAt(i)) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(numeros.charAt(9))) return false;

  // Valida segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(numeros.charAt(i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(numeros.charAt(10))) return false;

  return true;
};

/**
 * Formata CNPJ para exibição (aceita entrada com ou sem máscara)
 * Retorna formato XX.XXX.XXX/XXXX-XX
 */
export const formatarCnpj = (value: string): string => {
  const numbers = value.replace(/\D/g, '');

  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 5) {
    return numbers.replace(/^(\d{2})(\d{0,3})$/, '$1.$2');
  } else if (numbers.length <= 8) {
    return numbers.replace(/^(\d{2})(\d{3})(\d{0,3})$/, '$1.$2.$3');
  } else if (numbers.length <= 12) {
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})$/, '$1.$2.$3/$4');
  } else if (numbers.length <= 14) {
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})$/, '$1.$2.$3/$4-$5');
  }

  return numbers.slice(0, 14).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Alias para normalizarCPF - mantém compatibilidade
 */
export const normalizarCpf = normalizarCPF;